import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'I18n/utils/getLang';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Link } from 'foundations-components/transitional/components/Link';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { Tag } from 'foundations-components/transitional/components/Tag';
import { Fragment } from 'react';
import { useDetailsPageContext } from 'marketplace-ui-common/context/DetailsPageContext';
import { useFeatureCompatibilities } from '../hooks/useFeatureCompatibilities';
import { useFeaturePlanCombinations } from '../hooks/useFeaturePlanCombinations';
import { getHubSpotPricingUrl } from 'marketplace-ui-common/utils/DetailsPageUrls';
import CompatibilityIcon from './CompatibilityIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function PlanCombinationsModalFeatureSection({
  featureName,
  featureCompatibilitiesData,
  featurePlanCombinationsData
}) {
  const {
    appContext
  } = useDetailsPageContext();
  const isPublic = appContext === 'public';
  const compatibilityData = featureCompatibilitiesData[featureName];
  const planCombinationsData = featurePlanCombinationsData[featureName];
  const isCompatible = compatibilityData === null || compatibilityData === void 0 ? void 0 : compatibilityData.satisfied;
  const satisfyingCombos = planCombinationsData === null || planCombinationsData === void 0 ? void 0 : planCombinationsData.satisfyingCombos;
  let renderedCompatiblity = isCompatible ? /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    flexDirection: "row",
    gap: "200",
    children: [/*#__PURE__*/_jsx(CompatibilityIcon, {
      compatibilityType: "COMPATIBLE_ALL"
    }), /*#__PURE__*/_jsx(Text, {
      color: "text-core-subtle",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.compatibility.combinationsModal.compatible"
      })
    })]
  }) : /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    flexDirection: "row",
    gap: "200",
    children: [/*#__PURE__*/_jsx(CompatibilityIcon, {
      compatibilityType: "COMPATIBLE_NONE"
    }), /*#__PURE__*/_jsx(Text, {
      color: "text-core-subtle",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.compatibility.combinationsModal.notCompatible"
      })
    })]
  });
  if (isPublic) {
    renderedCompatiblity = /*#__PURE__*/_jsx(Text, {
      color: "text-core-subtle",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.compatibility.combinationsModal.public"
      })
    });
  }
  return /*#__PURE__*/_jsxs(View, {
    borderColor: "border-core-subtle",
    borderRadius: "300",
    borderStyle: "solid",
    borderWidth: "100",
    padding: "400",
    children: [/*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      flexDirection: "row",
      justifyContent: "space-between",
      marginBlockEnd: "200",
      inlineSize: "100%",
      children: [renderedCompatiblity, /*#__PURE__*/_jsx(Badge, {
        alignItems: "center",
        use: "pantera",
        children: /*#__PURE__*/_jsx(Text, {
          typography: "heading-25",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.compatibility.combinationsModal.feature"
          })
        })
      })]
    }), /*#__PURE__*/_jsx(Text, {
      marginBlockEnd: "200",
      typography: "heading-100",
      children: featureName
    }), /*#__PURE__*/_jsx(Text, {
      marginBlockEnd: "200",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.compatibility.combinationsModal.availableWith"
      })
    }), /*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      gap: "200",
      children: satisfyingCombos.map((combo, comboIndex) => /*#__PURE__*/_jsx(Flex, {
        alignItems: "center",
        backgroundColor: "fill-surface-recessed",
        borderColor: "border-core-subtle",
        borderRadius: "300",
        borderStyle: "solid",
        borderWidth: "100",
        flexDirection: "row",
        flexWrap: "wrap",
        gap: "100",
        padding: "300",
        children: combo.and.map((and, planIndex) => /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx(Tag, {
            children: /*#__PURE__*/_jsx(Text, {
              typography: "heading-25",
              children: and.displayName || 'HubSpot Free'
            })
          }), planIndex < combo.and.length - 1 && /*#__PURE__*/_jsx(Text, {
            children: "+"
          })]
        }, planIndex))
      }, comboIndex))
    })]
  });
}
export function PlanCombinationsModal({
  features,
  onClose
}) {
  const {
    appContext,
    productType
  } = useDetailsPageContext();
  const {
    result: featureCompatibilitiesData,
    loading: featureCompatibilitiesLoading,
    error: featureCompatibilitiesError
  } = useFeatureCompatibilities({
    appContext,
    features,
    productType
  });
  const {
    result: featurePlanCombinationsData,
    loading: featurePlanCombinationsLoading,
    error: featurePlanCombinationsError
  } = useFeaturePlanCombinations({
    appContext,
    features,
    productType
  });
  let renderedFeatures;
  if (featureCompatibilitiesLoading || featurePlanCombinationsLoading) {
    renderedFeatures = /*#__PURE__*/_jsx(LoadingSpinner, {});
  } else if (featureCompatibilitiesError || featurePlanCombinationsError) {
    renderedFeatures = /*#__PURE__*/_jsx(Alert, {
      type: "danger",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.compatibility.combinationsModal.error"
      })
    });
  } else {
    renderedFeatures = Object.keys(featurePlanCombinationsData).map(featureName => /*#__PURE__*/_jsx(PlanCombinationsModalFeatureSection, {
      featureCompatibilitiesData: featureCompatibilitiesData,
      featurePlanCombinationsData: featurePlanCombinationsData,
      featureName: featureName
    }, featureName));
  }
  return /*#__PURE__*/_jsxs(Modal, {
    inlineSize: "50%",
    maxInlineSize: "840px",
    children: [/*#__PURE__*/_jsx(DialogCloseButton, {
      onClick: onClose
    }), /*#__PURE__*/_jsx(DialogHeader, {
      children: /*#__PURE__*/_jsx(Text, {
        as: "h1",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.compatibility.combinationsModal.header"
        })
      })
    }), /*#__PURE__*/_jsxs(DialogBody, {
      paddingBlockEnd: "800",
      maxBlockSize: "75vh",
      children: [/*#__PURE__*/_jsxs(Flex, {
        flexDirection: "row",
        flexWrap: "wrap",
        marginBlockEnd: "500",
        children: [/*#__PURE__*/_jsx(Text, {
          marginInlineEnd: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.compatibility.combinationsModal.subheader"
          })
        }), /*#__PURE__*/_jsx(Link, {
          external: true,
          href: `${getHubSpotPricingUrl(getLang())}?upgradeSource=marketplace-plan-combinations`,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.compatibility.combinationsModal.link"
          })
        })]
      }), /*#__PURE__*/_jsx(Flex, {
        flexDirection: "column",
        gap: "400",
        children: renderedFeatures
      })]
    })]
  });
}